/* eslint-disable no-unneeded-ternary */
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// @mui
import { Collapse, Divider } from '@mui/material';
// components
import NavItem from './NavItem';
import { tidyDividers } from '../tidyDividers';
// hooks
import useActiveLink from '../../../hooks/useActiveLink';

const NavList = ({ data, depth, hasChildren }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const hasNestedItems = hasChildren && Array.isArray(data?.children) && data.children.length > 0;
    const { active, isExternalLink } = useActiveLink(data?.path, !hasNestedItems);

    const matchExactPath = (targetPath) => targetPath && pathname === targetPath;
    const matchChildPath = (targetPath) => {
        if (!targetPath) {
            return false;
        }
        return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
    };

    const hasActiveChild = hasNestedItems && data.children.some((child) => matchChildPath(child.path));
    const shouldBeOpen = hasNestedItems ? (hasActiveChild || matchExactPath(data?.path)) : false;

    const [open, setOpen] = useState(shouldBeOpen);

    useEffect(() => {
        if (hasNestedItems) {
            setOpen(shouldBeOpen);
        }
    }, [hasNestedItems, shouldBeOpen]);

    const handleClickItem = () => {
        if (!hasChildren) {
            navigate(data?.path);
        }
        setOpen(!open);
    };

    return (
        <>
            <NavItem
                item={data}
                depth={depth}
                open={open}
                active={active}
                isExternalLink={isExternalLink}
                onClick={handleClickItem}
            />

            {hasChildren && (
                <Collapse in={open} unmountOnExit>
                    <NavSubList data={data.children} depth={depth} />
                </Collapse>
            )}
        </>
    );
};

const NavSubList = ({ data, depth }) => (
    <>
        {tidyDividers(data).map((list, index) => {
            if (list.divider) {
                // Hairline between two real items (never leading / trailing / doubled).
                return <Divider key={`divider-${index}`} data-testid="nav-divider" sx={{ my: 0.5, mx: 2, borderStyle: 'solid', borderColor: 'divider' }} />;
            }
            return (
                <NavList
                    key={list.title + list.path}
                    data={list}
                    depth={depth + 1}
                    hasChildren={!!list.children}
                />
            );
        })}
    </>
);

NavSubList.propTypes = {
    data: PropTypes.array,
    depth: PropTypes.number,
};

NavList.propTypes = {
    data: PropTypes.object,
    depth: PropTypes.number,
    hasChildren: PropTypes.bool,
};

export default NavList;
